import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { ShoppingCart, ArrowLeft } from "lucide-react";
import ReviewSection from "@/components/ReviewSection";

function imageOf(path) {
  if (!path) return null;
  if (typeof path === "string" && path.startsWith("http")) return path;
  return fileUrl(path);
}

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [active, setActive] = useState(0);
  const [adding, setAdding] = useState(false);
  const autofocusReview = typeof window !== "undefined" && window.location.hash === "#review-form";

  useEffect(() => { api.get(`/products/${id}`).then(r => setProduct(r.data)).catch(() => setProduct(false)); }, [id]);

  if (product === null) return <div className="pt-28 text-center text-zinc-500">Loading…</div>;
  if (product === false) return <div className="pt-28 text-center text-zinc-500" data-testid="product-not-found">Product not found.</div>;

  const onAdd = async () => {
    if (!user) { navigate(`/login?next=/product/${id}`); return; }
    setAdding(true);
    try { await add(product.id, 1); toast.success("Added to cart"); }
    catch { toast.error("Could not add to cart"); }
    finally { setAdding(false); }
  };

  const fallback = "https://static.prod-images.emergentagent.com/jobs/d3a3c830-7d3a-49ef-ad18-3f342dc4beda/images/61fe03154047828097ebbc7961c5f590c8897894b9ea6fa9e207bb0d1fee5801.png";
  const imgs = (product.images && product.images.length) ? product.images : [fallback];

  return (
    <div data-testid="product-detail-page" className="pt-28 pb-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <button onClick={() => navigate(-1)} data-testid="back-btn" className="text-xs uppercase tracking-widest text-zinc-500 hover:text-white inline-flex items-center gap-2 mb-8"><ArrowLeft className="w-4 h-4" />Back</button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div>
          <div className="aspect-[4/5] w-full bg-[#0A0A0A] overflow-hidden border border-white/5">
            <img src={imageOf(imgs[active])} alt={product.title} className="w-full h-full object-cover" />
          </div>
          {imgs.length > 1 && (
            <div className="flex gap-3 mt-4">
              {imgs.map((img, i) => (
                <button key={i} onClick={() => setActive(i)} data-testid={`thumb-${i}`} className={`w-20 h-20 border ${i === active ? "border-[#FF007F]" : "border-white/10"}`}>
                  <img src={imageOf(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <span className="badge-neon">{product.category}</span>
          <h1 data-testid="product-title" className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-4">{product.title}</h1>
          <p className="text-xs uppercase tracking-widest text-zinc-500 mt-3">by {product.seller_name}</p>
          <p data-testid="product-price" className="font-display font-black text-5xl text-[#FF007F] text-glow-pink mt-8">${Number(product.price).toFixed(2)}</p>
          <p className="text-zinc-400 leading-relaxed mt-6 whitespace-pre-line">{product.description}</p>

          <div className="flex flex-wrap gap-2 mt-6">
            {(product.tags || []).map((t) => <span key={t} className="text-xs text-zinc-500 border border-white/10 px-3 py-1 uppercase tracking-wider">{t}</span>)}
          </div>

          <div className="mt-10 flex gap-3">
            <button data-testid="add-to-cart-btn" onClick={onAdd} disabled={adding} className="btn-primary inline-flex items-center gap-2">
              <ShoppingCart className="w-4 h-4" /> {adding ? "Adding…" : "Add to cart"}
            </button>
            <button data-testid="buy-now-btn" onClick={async () => { await onAdd(); navigate("/cart"); }} className="btn-secondary">Buy now</button>
          </div>

          <p className="text-xs text-zinc-600 mt-6">Stock: {product.stock} · Listed {new Date(product.created_at).toLocaleDateString()}</p>
        </div>
      </div>

      <ReviewSection product={product} autofocus={autofocusReview} />
    </div>
  );
}
